import { type TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps } from 'react';

import {
  BenchPlayerScore,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import useFeatureFlags from '@sorare/core/src/hooks/useFeatureFlags';

import { BasicBenchExtraWrapper } from 'components/benchObject/BasicBenchExtraWrapper';
import { CardFooter } from 'components/card/CardFooter';
import { SellCommonCard } from 'components/commonCard/SellCommonCard';
import { TokenTransferTypeIcon } from 'components/token/TokenTransferTypeIcon';
import { TokenTransferTypeLabel } from 'components/token/TokenTransferTypeLabel';

import { TokenRoot } from '../TokenRoot';
import type { GalleryTokenContent_anyCard } from './__generated__/index.graphql';

type Props = {
  anyCard: GalleryTokenContent_anyCard;
  onInfoClick?: () => void;
} & Pick<
  ComponentProps<typeof TokenRoot>,
  | 'action'
  | 'onToggleCardToDrop'
  | 'selectedToDrop'
  | 'hideDetails'
  | 'stackedTokensCount'
  | 'canSellCommon'
>;

export const GalleryTokenContent = ({
  anyCard,
  canSellCommon,
  onToggleCardToDrop,
  selectedToDrop,
  onInfoClick,
  ...rootProps
}: Props) => {
  const { sport } = useSportContext();
  const {
    flags: { useMlbSeasonAverageScore = true },
  } = useFeatureFlags();

  const modePerSport: Record<Sport, BenchPlayerScore> = {
    [Sport.FOOTBALL]: BenchPlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
    [Sport.NBA]: BenchPlayerScore.LAST_TEN_PLAYED_SO5_AVERAGE_SCORE,
    [Sport.BASEBALL]: useMlbSeasonAverageScore
      ? BenchPlayerScore.SEASON_AVERAGE_SCORE
      : BenchPlayerScore.LAST_FIFTEEN_SO5_AVERAGE_SCORE,
  };
  const mode = modePerSport[sport];

  return (
    <TokenRoot
      anyCard={anyCard}
      canSellCommon={canSellCommon}
      onToggleCardToDrop={onToggleCardToDrop}
      selectedToDrop={selectedToDrop}
      {...rootProps}
    >
      <Vertical gap={1.5}>
        <CardFooter
          anyCard={anyCard}
          benchPlayerScore={mode}
          extra={
            anyCard.tokenOwner && (
              <BasicBenchExtraWrapper height={3}>
                <Horizontal gap={0.5}>
                  <TokenTransferTypeIcon
                    size={1.5}
                    transferType={anyCard.tokenOwner.transferType}
                  />
                  <LabelS color="var(--c-nd-600)">
                    <TokenTransferTypeLabel
                      transferType={anyCard.tokenOwner.transferType}
                    />
                  </LabelS>
                </Horizontal>
              </BasicBenchExtraWrapper>
            )
          }
          onClick={onInfoClick}
        />
        {canSellCommon && (
          <SellCommonCard
            card={anyCard}
            onSelect={() => onToggleCardToDrop?.(anyCard.slug)}
            selected={selectedToDrop}
          />
        )}
      </Vertical>
    </TokenRoot>
  );
};

GalleryTokenContent.fragments = {
  anyCard: gql`
    fragment GalleryTokenContent_anyCard on AnyCardInterface {
      slug
      tokenOwner {
        id
        transferType
      }
      ...TokenRoot_anyCard
      ...CardFooter_anyCardInterface
      ...SellCommonCard_anyCard
    }
    ${TokenRoot.fragments.anyCard}
    ${CardFooter.fragments.anyCardInterface}
    ${SellCommonCard.fragments.anyCard}
  ` as TypedDocumentNode<GalleryTokenContent_anyCard>,
};
